import {attr, target, controller} from '@github/catalyst'
import {html, render} from '@github-ui/jtml-shimmed'
import {isFullscreen} from './steps'

@controller
export class CheckStepElement extends HTMLElement {
  @attr name = ''
  @attr number = 1
  @attr conclusion = ''
  @attr externalId = ''
  @attr logUrl = ''
  @attr expand = ''
  @attr inProgress = false
  @attr startedAt = ''
  @attr completedAt = ''
  @attr jobCompleted = false
  @attr useActionsUx = false
  @attr loggedIn = false
  @target declare header: HTMLElement

  get detailsDisabled(): boolean {
    return !this.loggedIn || !this.logUrl
  }

  get iconTemplate() {
    const id = this.inProgress ? 'step-icon-in-progress' : `step-icon-${this.conclusion}`
    const template = document.getElementById(id) || document.getElementById('step-icon-other')

    if (!template) return

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return html([template.innerHTML] as any)
  }

  get gearIcon() {
    const template = document.getElementById('primer-octicon-gear')

    if (!template) return
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return html([template.innerHTML] as any)
  }

  get time() {
    if (this.startedAt && this.completedAt) {
      return this.preciseDuration()
    } else if (this.startedAt && !this.jobCompleted) {
      return html`<relative-time format="elapsed" datetime="${this.startedAt}">
        Started at ${new Date(this.startedAt).getTime()}
      </relative-time>`
    }
  }

  connectedCallback() {
    this.update()
  }

  preciseDuration() {
    const startedAt = new Date(this.startedAt)
    const completedAt = new Date(this.completedAt)
    const seconds = Math.round((completedAt.getTime() - startedAt.getTime()) / 1000)

    const minutes = Math.floor(seconds / 60)
    const remainderSeconds = seconds % 60

    const hours = Math.floor(minutes / 60)
    const remainderMinutes = minutes % 60

    const days = Math.floor(hours / 24)
    const remainderHours = hours % 24

    if (seconds < 0) {
      // don't display negative time
      // visual fix for: https://github.com/github/c2c-actions-checks/issues/311
      return '0s'
    } else if (minutes < 1) {
      return `${seconds}s`
    } else if (hours < 1) {
      return `${minutes}m ${remainderSeconds}s`
    } else if (days < 1) {
      return `${hours}h ${remainderMinutes}m ${remainderSeconds}s`
    } else {
      return `${days}d ${remainderHours}h`
    }
  }

  update() {
    const styles = []
    if (!this.useActionsUx) styles.push('position: sticky')
    if (this.conclusion === 'skipped') styles.push('cursor: default')

    // needed to persist proper sticky header value that gets calculated by setLogTitleStickyValues in logs.ts
    if (isFullscreen()) {
      styles.push('top: 0px')
    } else {
      if (this.header?.style?.cssText) styles.push(this.header.style.cssText)
    }

    render(
      html`<details
        class="Details-element CheckStep rounded-2 details-reset js-checks-log-details ${this.useActionsUx
          ? 'px-2'
          : 'pl-2 pr-1'}"
        id="check-step-${this.number}"
        data-step-number="${this.number}"
        data-conclusion="${this.conclusion}"
        data-external-id="${this.externalId}"
        style="${this.detailsDisabled ? 'pointer-events:none;' : ''}"
        data-log-url="${this.detailsDisabled ? '' : this.logUrl}"
        data-expand="${this.expand ? 'true' : ''}"
      >
        <summary
          class="CheckStep-header p-2 mb-1 rounded-2 js-check-step-summary ${this.useActionsUx
            ? 'uxr_CheckStep-header mx-2'
            : 'ml-2 top-0'}"
          style="${styles.join(';')}"
          data-retain-focus
          data-target="check-step.header"
          data-step-pending="${!this.conclusion}"
        >
          <div class="d-flex flex-items-center">
            ${this.loggedIn
              ? html`<svg
                  class="octicon octicon-chevron-right js-check-step-chevron mr-2 flex-shrink-0 CheckStep-chevron"
                  viewBox="0 0 16 16"
                  version="1.1"
                  width="16"
                  height="16"
                  aria-hidden="${!this.logUrl}"
                  style="${this.logUrl ? '' : 'visibility:hidden'}"
                >
                  <path
                    fill-rule="evenodd"
                    d="M6.22 3.22a.75.75 0 011.06 0l4.25 4.25a.75.75 0 010 1.06l-4.25 4.25a.75.75 0 01-1.06-1.06L9.94 8 6.22 4.28a.75.75 0 010-1.06z"
                  ></path>
                </svg>`
              : ''}

            <svg
              fill="none"
              height="16"
              viewBox="0 0 16 16"
              width="16"
              class="anim-rotate js-check-step-loader mr-2 flex-shrink-0"
              xmlns="http://www.w3.org/2000/svg"
              hidden
            >
              <g stroke-width="2">
                <circle cx="8" cy="8" r="7" stroke="#959da5"></circle>
                <path
                  d="m12.9497 3.05025c1.3128 1.31276 2.0503 3.09323 2.0503 4.94975 0 1.85651-.7375 3.637-2.0503 4.9497"
                  stroke="#fafbfc"
                ></path>
              </g>
            </svg>

            ${this.iconTemplate}

            <span class="flex-1 ml-n1 mr-1 css-truncate css-truncate-overflow user-select-none"> ${this.name} </span>

            <div class="text-mono text-normal text-small float-right">${this.time}</div>
          </div>
        </summary>

        <div class="js-checks-log-display">
          <div
            class="text-mono text-small pl-2 py-1 my-2 overflow-auto js-checks-log-display-error color-fg-muted color-bg-danger ml-2"
            hidden
          >
            <span class="text-bold color-fg-danger">Error: </span>
            <span
              class="js-checks-log-display-error-text"
              data-error-text="We are currently unable to download the log. Please try again later."
              data-not-found-text="The log was not found. It may have been deleted based on retention settings."
              data-removed-text="The logs for this run have expired and are no longer available. Logs may have been removed based on retention settings on the repo at the time the run was started."
            ></span>
          </div>
          <div
            class="text-mono text-small py-1 my-2 js-checks-log-display-container ${this.useActionsUx
              ? 'mx-2'
              : 'ml-2'}"
            hidden
          ></div>
          <div
            class="d-flex flex-items-center pl-2 py-1 my-2 text-small text-mono color-text-white js-checks-log-display-truncated"
            hidden
          >
            <div class="ml-1 color-fg-attention">
              This step has been truncated due to its large size. View the raw logs from the ${this.gearIcon} menu once
              the workflow run has completed.
            </div>
          </div>
        </div>
      </details>`,
      this,
    )
  }
}
